// We only need to import the modules necessary for initial render
import { setSubroute } from 'store/navbar'
import { studyLockActions } from 'store/studyLock'
import { browserHistory } from 'utils/navigation'
import { setPageTitle } from 'utils/misc'
import { isSameUser, checkUserHasScope } from 'utils/userUtils'
import { SITE_ROLE_IDS } from 'utils/constants'
import ViewStudyRoute from './ViewStudy'
import { actions, fetchStudyVersionList, setStudyVersionList } from '../CreateStudy/modules/CreateStudy'
import AnnouncementsRoute from './routes/Announcements'
import AuditRoute from './routes/Audit'
import CasesRoute from './routes/Cases'
import ClinroRoute from './routes/Clinro'
import ConsentsRoute from './routes/Consents'
import DataCorrectionRoute from './routes/DataCorrection'
import DataCorrectionV2Route from './routes/DataCorrectionV2'
import GeofencesRoute from './routes/Geofences'
import InstrumentsRoute from './routes/Instruments'
import NotificationsRoute from './routes/Notifications'
import ParticipantsRoute from './routes/Participants'
import PaymentsRoute from './routes/Payments'
import SitesRoute from './routes/Sites'
import StudyDashboardRoute from './routes/StudyDashboard'
import TrainingRoute from './routes/Training'
import DataInboxRoute from './routes/DataInbox'
import UsersRoute from './routes/Users'
import VirtualVisitsRoute from './routes/VirtualVisits'
import VisitSchedulesRoute from './routes/VisitSchedules'
import StudyTimelineRoute from './routes/StudyTimeline'
import { actions as paymentActions } from './routes/Payments/modules/Payments'
import { actions as visitSchedulesActions } from './routes/VisitSchedules/routes/VisitSchedulesPage/modules/VisitSchedules'
import { actions as notyActions } from 'layouts/ErrorBox'
import { generateNotyMessage } from 'utils/request'
import { getUserRoleIdByStudy } from '../../../../utils/misc'
import { USER_ROLE_IDS_WITH_LIMITED_SIDEBAR_ACCESS } from '../../../../utils/constants'
import {
  getSafeVersionId,
  getVersionID,
  isStudyVersionInList,
  normalizeStudyVersionId,
} from '../CreateStudy/utils/studyUtils'
import { getStudyVersionID } from 'utils/studyConfigUtils'

const proceedWithIndexRoute = ({ store, study, user, currStudyID, studyVersionID, cb }) => {
  const roleId = getUserRoleIdByStudy(user, currStudyID)
  if (USER_ROLE_IDS_WITH_LIMITED_SIDEBAR_ACCESS.includes(roleId)) {
    browserHistory.push(`/studies/${currStudyID}/${studyVersionID}/compliance`)
  }
  setPageTitle(`Configuration | Study ${currStudyID} | ${study.currentStudy.platform_name}`)
  cb(null, ViewStudyRoute(store))
}

const keepRequestedStudyVersion = (store, studyId, studyVersionID, versions = []) => {
  if (!studyVersionID || isStudyVersionInList(versions, studyVersionID)) return
  store.dispatch(
    setStudyVersionList(
      [
        ...(Array.isArray(versions) ? versions : []),
        {
          id: studyVersionID,
          version_id: studyVersionID,
        },
      ],
      studyId,
    ),
  )
}

const redirectAwayFromMissingVersion = ({
  store,
  study,
  user,
  currStudyID,
  studyVersionID,
  showToast,
}) => {
  const hasStudyPromotion = !!study?.currentStudy?.config?.study_promotion
  const fallbackVersionId = hasStudyPromotion
    ? getVersionID(user, study, currStudyID)
    : getSafeVersionId(study.studyVersionList?.[0])

  if (
    fallbackVersionId &&
    normalizeStudyVersionId(fallbackVersionId) !== normalizeStudyVersionId(studyVersionID)
  ) {
    if (showToast) {
      store.dispatch(
        notyActions.showError({
          text: generateNotyMessage(
            'The study version you are trying to access is no longer available.',
            false,
          ),
        }),
      )
    }
    browserHistory.replace(`/studies/${currStudyID}/${fallbackVersionId}`)
    return true
  }
  return false
}

/*  Note: Instead of using JSX, we recommend using react-router
    PlainRoute objects to build route definitions.   */

export default function createRoutes(store) {
  const { user } = store.getState()
  return {
    path: ':studyID/:studyVersionID',
    exact: true,
    getIndexRoute(location, cb) {
      if (isSameUser(user.id) ?? true) {
        store.dispatch(setSubroute('configuration'))

        // First fetch study versions
        store.dispatch(fetchStudyVersionList(location.params.studyID)).then(() => {
          const { study, user: currentUser } = store.getState()
          const currStudyID = location.params.studyID
          const versions = study.studyVersionList
          const studyVersionID = location.params.studyVersionID || getStudyVersionID(location)
          const listIsForThisStudy = String(study.studyVersionListStudyId) === String(currStudyID)

          // Check if current version exists (URLs may use id or version_id)
          const versionExists = listIsForThisStudy && isStudyVersionInList(versions, studyVersionID)

          // Handle case where versions are empty or version Exists
          if (versionExists || !versions?.length || !listIsForThisStudy) {
            proceedWithIndexRoute({
              store,
              study,
              user: currentUser,
              currStudyID,
              studyVersionID,
              cb,
            })
            return
          }

          store.dispatch(actions.fetchStudy(currStudyID)).then(() => {
            const currentState = store.getState()
            const hasStudyPromotion = !!currentState?.study?.currentStudy?.config?.study_promotion

            // After promote, versions API can briefly lag. Refetch once before treating the
            // URL version as unavailable (avoids false toast + bounce back to latest UAT).
            const resolveMissingVersion = () => {
              const refreshedState = store.getState()
              const refreshedStudy = refreshedState.study
              const refreshedVersions = refreshedStudy.studyVersionList
              const refreshedListIsForStudy =
                String(refreshedStudy.studyVersionListStudyId) === String(currStudyID)

              if (refreshedListIsForStudy && isStudyVersionInList(refreshedVersions, studyVersionID)) {
                proceedWithIndexRoute({
                  store,
                  study: refreshedStudy,
                  user: refreshedState.user,
                  currStudyID,
                  studyVersionID,
                  cb,
                })
                return
              }

              // Study promotion: after UAT→Live (etc.) the versions API can lag. Prefer keeping
              // the requested version over a false "no longer available" toast + UAT bounce.
              if (hasStudyPromotion) {
                keepRequestedStudyVersion(store, currStudyID, studyVersionID, refreshedVersions)
                proceedWithIndexRoute({
                  store,
                  study: store.getState().study,
                  user: refreshedState.user,
                  currStudyID,
                  studyVersionID,
                  cb,
                })
                return
              }

              const redirected = redirectAwayFromMissingVersion({
                store,
                study: refreshedStudy,
                user: refreshedState.user,
                currStudyID,
                studyVersionID,
                showToast: false,
              })

              if (!redirected) {
                proceedWithIndexRoute({
                  store,
                  study: refreshedStudy,
                  user: refreshedState.user,
                  currStudyID,
                  studyVersionID,
                  cb,
                })
              }
            }

            if (hasStudyPromotion) {
              store.dispatch(fetchStudyVersionList(currStudyID)).then(resolveMissingVersion)
              return
            }

            resolveMissingVersion()
          })
        })
      } else {
        browserHistory.push('/')
      }
    },
    getChildRoutes(location, cb) {
      const getChildren = () => {
        const currentState = store.getState() // Consistent naming
        const versions = currentState.study.studyVersionList
        const studyID = location.params.studyID
        const studyVersionID = location.params.studyVersionID || getStudyVersionID(location)
        const listIsForThisStudy = String(currentState.study.studyVersionListStudyId) === String(studyID)
        const versionExists = listIsForThisStudy && isStudyVersionInList(versions, studyVersionID)

        const finish = () => {
          cb(null, [
            AnnouncementsRoute(store),
            AuditRoute(store),
            CasesRoute(store),
            ClinroRoute(store),
            ConsentsRoute(store),
            DataCorrectionRoute(store),
            DataCorrectionV2Route(store),
            GeofencesRoute(store),
            InstrumentsRoute(store),
            NotificationsRoute(store),
            ParticipantsRoute(store),
            PaymentsRoute(store),
            SitesRoute(store),
            StudyDashboardRoute(store),
            TrainingRoute(store),
            DataInboxRoute(store),
            UsersRoute(store),
            VirtualVisitsRoute(store),
            VisitSchedulesRoute(store),
            StudyTimelineRoute(store),
          ])
        }

        if (listIsForThisStudy && versions?.length > 0 && studyVersionID && !versionExists) {
          // Redirect to an available version while preserving sub-route.
          // Do not toast here: Preview opens in a new tab and cold-runs this guard, which
          // produced false-positive errors on Study Promotion studies while the page still loaded.
          const pathname = location.location?.pathname || location.pathname || ''
          const subPath = pathname
            .split('/')
            .slice(4)
            .join('/')

          const hasStudyPromotion = !!currentState?.study?.currentStudy?.config?.study_promotion

          const redirectIfStillMissing = () => {
            const refreshedState = store.getState()
            const refreshedStudy = refreshedState.study
            const refreshedVersions = refreshedStudy.studyVersionList
            const refreshedListIsForStudy =
              String(refreshedStudy.studyVersionListStudyId) === String(studyID)

            if (refreshedListIsForStudy && isStudyVersionInList(refreshedVersions, studyVersionID)) {
              finish()
              return
            }

            if (hasStudyPromotion) {
              keepRequestedStudyVersion(store, studyID, studyVersionID, refreshedVersions)
              finish()
              return
            }

            const fallbackVersionId = getSafeVersionId(refreshedVersions?.[0] || versions[0])

            if (
              fallbackVersionId &&
              normalizeStudyVersionId(fallbackVersionId) !== normalizeStudyVersionId(studyVersionID)
            ) {
              browserHistory.replace(`/studies/${studyID}/${fallbackVersionId}/${subPath}`)
            }
            finish()
          }

          if (hasStudyPromotion) {
            store.dispatch(fetchStudyVersionList(studyID)).then(redirectIfStillMissing)
            return
          }

          redirectIfStillMissing()
          return
        }

        finish()
      }

      if (isSameUser(user.id) ?? true) {
        const routeStudyId = location.params.studyID
        const currentStudyId = store.getState().study.currentStudy?.id
        const shouldFetchStudy =
          routeStudyId != null && String(currentStudyId ?? '') !== String(routeStudyId)

        if (shouldFetchStudy) {
          store.dispatch(actions.fetchStudy(routeStudyId)).then(study => {
            if (
              !(location.location?.pathname || location.pathname || '').endsWith('/payments') &&
              checkUserHasScope({ user, studyId: location.params.studyID, scope: ['rp', 'rp-no-pii'] })
            ) {
              if (study?.config?.super_gems) {
                store.dispatch(paymentActions.fetchPendingGiftcardsAndDonations(location.params.studyID, false))
              } else {
                store.dispatch(paymentActions.fetchPayments(location.params.studyID, false))
              }
            }

            if (
              study?.config?.visit_support &&
              SITE_ROLE_IDS.includes(user?.permissions?.[location.params.studyID]?.role_id) &&
              checkUserHasScope({ user, studyId: location.params.studyID, scope: ['rppv'] })
            ) {
              // Silent: Study-route sidebar prefetch. On Study Promotion + Preview (new tab),
              // a failing previous_visits call showed an error toast while preview still loaded.
              store.dispatch(
                visitSchedulesActions.fetchScheduleVisitsPrevious(location.params.studyID, () => {}, {
                  silent: true,
                }),
              )
            }

            const isLocked = study?.is_locked ? study?.is_locked : study?.config?.is_locked
            store.dispatch(studyLockActions.setStudyLock(isLocked))

            store.dispatch(fetchStudyVersionList(location.params.studyID)).then(() => {
              getChildren()
            })
          })
        } else {
          store.dispatch(fetchStudyVersionList(location.params.studyID)).then(() => {
            getChildren()
          })
        }
      } else browserHistory.push('/')
    },
  }
}
